/**
 * Remaps each item's `accountId` through any active per-account preview
 * override, so the outgoing batch preview request asks the server for the
 * OVERRIDDEN account's numbers rather than the real submitting account's.
 *
 * `overrides` is keyed by the item's real account id (one of the ticket's
 * `accountIds`) with the value being the account id the user wants to
 * preview under instead — `undefined` means no override is active for that
 * real account. An item with no `accountId` of its own falls back to
 * `fallbackAccountId` (matching how the rest of order-form-summary resolves
 * a missing per-row accountId) before the override lookup.
 *
 * The fallback fill ALWAYS runs, single-account ticket or not — a row added
 * through a flow that never set its own `accountId` (most of them predate
 * cross-exchange rows and still don't) has no other way to become
 * previewable, and `buildBatchOrderPreviewRequest`/`isRowPreviewable` drop
 * any row whose resolved accountId is empty. Dropping that fill for a
 * multi-account ticket silently blanked EVERY row's preview, not just the
 * one missing an accountId, since a preview request with zero previewable
 * rows returns null for the whole batch.
 *
 * `isSingleAccount` mirrors the `allowAccountOverride={accountIds.length ===
 * 1}` gate on the preview panel's override UI — the control that sets an
 * override is only ever shown (and clearable) when the whole ticket is one
 * account, so only the OVERRIDE SUBSTITUTION (not the fallback fill above)
 * is gated by that same condition. Without this, a stale override set while
 * the ticket was single-account would keep silently remapping that
 * account's rows after a row for a different account gets added, with no
 * visible control left to notice or clear it.
 */
export function applyAccountOverrides<T extends { accountId?: string }>(
  items: T[],
  overrides: Record<string, string | undefined>,
  fallbackAccountId: string,
  isSingleAccount: boolean
): T[] {
  return items.map((item) => {
    const realAccountId = item.accountId ?? fallbackAccountId
    const override = isSingleAccount ? overrides[realAccountId] : undefined
    return { ...item, accountId: override ?? realAccountId }
  })
}
